import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';
import { useDebouce } from 'hooks/debounce';

import { MainForm } from './main';
import { SidebarForm } from './sidebar';
import styles from './sidebar.module.css';

import { delay, isHTMLBodyEmpty } from 'app/shared/utils/general';
import { campaignValidationConfig } from 'app/shared/utils/form/validation/campaign';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import useValidator from 'hooks/validator';
import { useCampaignService } from 'hooks/mailing/campaign';
import { useAudienceService } from 'hooks/users/audience';
import { useMailboxService } from 'hooks/users/mailbox';
import { useDraftService } from 'hooks/mailing/draft';
import { useEventsService } from 'hooks/automation/event';
import { useMailingConfigService } from 'hooks/mailing/configuration';
import { useMailingRampService } from 'hooks/mailing/ramp';
import { useContactService } from 'hooks/users/contact';
import { useAITextService } from 'hooks/ai/text';
import { deleteDraftFromStorage, fetchDraftFromStorage, saveDraftToStorage } from './db/indexdb';
import GmModal from 'app/shared/modal/modal';

export const CreateCampaign = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { clearTimer, debounce } = useDebouce();
  const { checkSpaminess } = useAITextService();
  const { fetchAudiences } = useAudienceService();
  const { createCampaign, fetchCampaign, prepareTestCampaignData } = useCampaignService();
  const { fetchContacts } = useContactService();
  const { createDraft, updateDraft } = useDraftService();
  const { fetchEvents } = useEventsService();
  const { fetchMailboxes } = useMailboxService();
  const { validateRecords } = useMailingConfigService();
  const { fetchRamps } = useMailingRampService();

  const [audiences, setAudiences] = useState([]);
  const [campaign, setCampaign] = useState({});
  const [campaign_id, setCampaignId] = useState(id);
  const [contact_count, setContactCount] = useState(0);
  const [draft_status, setDraftStatus] = useState('');
  const [events, setEvents] = useState([]);
  const [mailbox, setMailbox] = useState({ emails: [] });
  const [loading, setLoading] = useState(false);
  const [ramp_status, setRampStatus] = useState('');
  const [send_to_test_audience, setSendToTestAudience] = useState(false);
  const [send_campaign, setSendCampaign] = useState(false);
  const [show_details_modal, setShowDetailsModal] = useState(false);
  const [show_dns_warning, setShowDNSWarning] = useState(false);
  const [show_ramp_warning, setShowRampWarning] = useState(false);
  const [show_spam_warning, setShowSpamWarning] = useState(false);
  const [spaminess, setSpaminess] = useState({});
  const [test_audience, setTestAudience] = useState({});

  const { errors, validateField, form_is_valid } = useValidator(campaignValidationConfig);

  useEffect(() => {
    window.addEventListener('online', saveDraft);

    return () => {
      window.removeEventListener('online', saveDraft);
    };
  }, []);

  useEffect(() => {
    if (id) {
      Promise.all([fetchCampaign(campaign_id), fetchDraftFromStorage(id)]).then(
        ([{ campaign }, campaign_in_storage]) => {
          let campaign_to_use = campaign;
          if (
            campaign_in_storage &&
            Date.parse(campaign.updated_on) < campaign_in_storage.last_modified
          ) {
            campaign_to_use = campaign_in_storage.content;
          }

          campaign_to_use.mailing_lists = [];
          Object.keys(campaign_to_use).forEach(function (key) {
            validateField(key, campaign_to_use[key]);
          });
          setCampaign(campaign_to_use);
        }
      );
    }

    fetchAudiences({
      query_string: 'is_archived=0&return_only=name,id,is_test_audience'
    }).then(({ audiences }) => {
      setAudiences(audiences);
      setTestAudience(() => audiences.find((audience) => audience.is_test_audience));
    });

    fetchMailboxes().then(({ mailboxes }) => {
      setMailbox(mailboxes[0] || { emails: [] });
    });

    fetchEvents({ query_string: 'type=user' }).then(({ events }) => {
      setEvents(events.map((event) => ({ ...event, label: event.name, value: event.code })));
    });

    fetchContacts({ query_string: 'count=1' }).then(({ size }) => {
      setContactCount(size);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAttributeChange = (attr_key, attr_value) => {
    if (attr_key === 'mailing_lists' && test_audience && attr_value[0] === test_audience.id) {
      validateField('schedule', campaign.schedule || {}, { is_test_campaign: true });
    } else if (attr_key === 'mailing_lists') {
      validateField('schedule', campaign.schedule || {}, { is_test_campaign: false });
    }

    validateField(attr_key, attr_value);
    const new_value = { ...campaign, [attr_key]: attr_value };
    setCampaign(() => new_value);
    debounce(() => saveDraft(new_value));
  };

  const handleDNSProceed = (can_proceed) => {
    setShowDNSWarning(false);
    setLoading(false);
    if (!can_proceed) return;

    setLoading(true);
    if (is_test_audience_selected) {
      setSendToTestAudience(true);
    } else {
      setSendCampaign(true);
    }
  };

  const handleSave = async (proceed) => {
    setShowSpamWarning(false);
    if (!proceed) {
      setLoading(false);
      return;
    }

    const sender_domain = campaign.sender_email.split('@')[1];
    if (contact_count > 1000) {
      const { ramps } = await fetchRamps();
      const ramp = ramps.find((ramp) => ramp.sender_email.split('@')[1] === sender_domain);
      if (ramp && ramp.status !== 'done') {
        setRampStatus(ramp.status);
        setLoading(false);
        setShowDetailsModal(false);
        setShowRampWarning(true);
        return;
      }
    }

    const validation_result = await validateRecords({
      data: { domain: sender_domain }
    });

    if (!validation_result.payload) {
      setLoading(false);
      setShowDetailsModal(false);
      setShowDNSWarning(true);
      return;
    }

    setShowDetailsModal(false);
    if (is_test_audience_selected) {
      setSendToTestAudience(true);
      return;
    }

    setSendCampaign(true);
  };

  const handleSendCampaignToTestAudience = async (proceed) => {
    setSendToTestAudience(false);
    setLoading(false);
    if (!proceed) return;

    setLoading(true);
    const { data } = prepareTestCampaignData(campaign);
    const response = await createCampaign({ data });

    if (!response) {
      toast.error('Unable to send test campaign. Please, try again');
      setLoading(false);
      return;
    }

    setLoading(false);
    toast.success(`Campaign queued successfully.`);
    await delay(0.5);
    navigate('/campaigns');
  };

  const handleSendCampaign = async (proceed) => {
    setSendCampaign(false);
    setLoading(false);
    if (!proceed) return;

    setLoading(true);
    const data = { ...campaign, status: 'queued' };
    const response = campaign_id
      ? await updateDraft(campaign_id, {
          data
        })
      : await createCampaign({ data });

    if (!response) {
      toast.error(`Unable to enqueue campaign at this moment. Please try again.`);
      setLoading(false);
      return;
    }

    setLoading(false);
    toast.success(`Campaign enqueued successfully.`);
    await deleteDraftFromStorage(campaign_id || response.id);
    navigate('/campaigns');
  };

  const handleSpamCheck = async () => {
    if (!form_is_valid) {
      toast.error('Please fix validation errors before sending.');
      return;
    }

    clearTimer();

    if (loading) return;

    if (!mailbox.emails.includes(campaign.sender_email)) {
      toast.error('Sender Email not verified.');
      return;
    }

    if (!campaign.body || isHTMLBodyEmpty(campaign.body)) {
      toast.error('Campaign body cannot be empty.');
      return;
    }

    setLoading(true);
    const data = {
      text: `
        Subject: ${campaign.subject}
        
        Body:
        ${campaign.body}
      `
    };

    const spaminess_result = await checkSpaminess({ data });
    if (spaminess_result && spaminess_result.score > 3) {
      setSpaminess(spaminess_result);
      setShowDetailsModal(false);
      setShowSpamWarning(true);
      return;
    }

    await handleSave(true);
  };

  const saveDraft = async (campaign) => {
    if (draft_status) return;
    const data = { ...campaign };

    setDraftStatus('Saving draft');
    if (campaign_id) {
      await saveDraftToStorage(campaign_id, data);
    }

    const response = campaign_id
      ? await updateDraft(campaign_id, { data })
      : await createDraft({ data });

    if (!response) {
      setDraftStatus('Unable to save draft');
    } else if (!campaign_id) {
      setCampaignId(() => response.id);
      setDraftStatus('Draft saved.');
    }

    await delay(1);
    setDraftStatus('');
  };

  const is_test_audience_selected =
    campaign.mailing_lists?.length > 0 && campaign.mailing_lists[0] === test_audience?.id;

  const is_continue_disabled =
    loading || !campaign.subject?.trim() || !campaign.body || isHTMLBodyEmpty(campaign.body);

  const is_send_disabled =
    loading ||
    !campaign.subject?.trim() ||
    !campaign.body ||
    isHTMLBodyEmpty(campaign.body) ||
    !campaign.sender_email?.trim() ||
    !mailbox.emails.includes(campaign.sender_email) ||
    !campaign.sender_name?.trim() ||
    !campaign.mailing_lists?.length ||
    (!is_test_audience_selected && !campaign.schedule?.date);

  return (
    <>
      <MainForm
        onContinue={() => setShowDetailsModal(true)}
        onEditorChange={handleAttributeChange}
        onTitleChange={(v) => handleAttributeChange('subject', v)}
        titleValue={campaign.subject}
        value={campaign.body}
        draft_status={draft_status}
        is_continue_disabled={is_continue_disabled}
      />

      <GmModal
        bodyClassName={styles.detailsModal}
        show_header={false}
        show_modal={show_details_modal}
        onClose={() => setShowDetailsModal(false)}
        position="right"
      >
        <SidebarForm
          audiences={audiences}
          events={events}
          test_audience={test_audience}
          is_test_audience_selected={is_test_audience_selected}
          campaign={campaign}
          onChange={handleAttributeChange}
          mailbox={mailbox}
          setMailbox={setMailbox}
          draft_status={draft_status}
          errors={errors}
          onSave={handleSpamCheck}
          is_saving={loading}
          is_save_disabled={is_send_disabled}
        />
      </GmModal>

      <ConfirmationDialog
        icon_name="info"
        title="Send campaign to test audience?"
        message="You are about to send this campaign to only your test audience. Note that it won't be sent to other contacts not within this audience list"
        callback={handleSendCampaignToTestAudience}
        is_open={send_to_test_audience}
      />

      <ConfirmationDialog
        icon_name="info"
        title="Send Campaign"
        message="Are you sure you want to send this campaign?"
        callback={handleSendCampaign}
        is_open={send_campaign}
      />

      <ConfirmationDialog
        title={` 🚨 🚨 Spam Risk Detected (${spaminess?.score || 0}/10)`}
        can_proceed={spaminess?.score < 7}
        message={
          spaminess?.score > 7 ? (
            <>
              <p>
                We’ve detected a high spam risk in your email <b>(score: {spaminess?.score}/10)</b>.
              </p>
              <br />
              <p>
                This level of risk means the email is very likely to:
                <ul>
                  <li>Land in the Spam folder for most recipients</li>
                  <li>
                    Trigger spam complaints or blocks from providers like Gmail, Outlook, or Yahoo
                  </li>
                  <li>Harm your sender reputation and future deliverability</li>
                </ul>
              </p>
              <p>
                To protect your domain, IP reputation, and our shared sending pool, we do not allow
                campaigns with this score to be sent.
              </p>
              <br />
              <p>
                <b>Top issues found:</b>
              </p>
              <ul>
                {(spaminess?.suggestions || []).map((suggestion, index) => (
                  <li key={`spam-suggestion-${index}`}>{suggestion}</li>
                ))}
              </ul>
              <br />
              <p>
                After editing, re-check the spam score. Once it’s 7 or below, you’ll be able to send
                your campaign.
              </p>
              <br />
            </>
          ) : (
            <>
              <p>
                Your email looks mostly fine, but it has a few elements that could cause it to land
                in the Promotions tab or Spam folder for some people.
              </p>
              <br />
              <p>
                <b>Top issues found:</b>
              </p>
              <ul>
                {(spaminess?.suggestions || []).map((suggestion, index) => (
                  <li key={`spam-suggestion-${index}`}>{suggestion}</li>
                ))}
              </ul>
              <br />
              <p>
                Most emails in this range still reach the inbox — these tweaks will help push it
                toward Primary.
              </p>
              <br />
              <p>
                Ready to send anyway?{' '}
                <b>You can proceed, but we recommend fixing first for best results.</b>
              </p>
            </>
          )
        }
        callback={handleSave}
        is_open={show_spam_warning}
        wrapperClassName={styles.spamDialog}
      />

      <ConfirmationDialog
        title="⚠️ Misconfigured DNS"
        can_proceed={contact_count < 2500}
        action_text="Send anyway."
        message={
          <>
            <p>
              There are issues with your DNS configuration that may negatively impact email delivery
              and harm your sending reputation.{' '}
              {contact_count >= 2500 &&
                `As a result, you won’t be able to send this campaign at the moment.`}
            </p>
            <br />
            <p>
              Please contact your IT team to review and correct the DNS configuration before trying
              again.
            </p>
          </>
        }
        callback={handleDNSProceed}
        is_open={show_dns_warning}
      />

      <ConfirmationDialog
        title={
          ramp_status === 'started'
            ? '💡 Domain Warm-up in Progress'
            : '⚠️ Domain Warm-up Not Started'
        }
        can_proceed={false}
        message={
          <>
            {ramp_status === 'started' ? (
              <>
                <p>
                  Your sending domain <b>({campaign.sender_email.split('@')[1]})</b> is in the
                  warm-up phase. We're gradually increasing the number of emails sent to build trust
                  with Gmail, Outlook, Yahoo, and other providers.
                </p>
                <p>
                  Important:
                  <ul>
                    <li>
                      Transactional emails (order confirmations, password resets, etc.) are sending
                      normally
                    </li>{' '}
                    <li>
                      Promotional emails, newsletters, and bulk campaigns are paused until warm-up
                      completes
                    </li>
                  </ul>
                </p>
                <br />
                <p>
                  If you have any questions, kindly <b>contact support (support@go-mailer.com)</b>.
                </p>
              </>
            ) : (
              <>
                <p>
                  We haven't started warming up your sending domain yet. To protect your
                  deliverability and avoid landing in spam folders, we need to gradually increase
                  sending volume over the next <b>2–4 weeks</b>.
                </p>
                <p>
                  What happens during warm-up:
                  <ul>
                    <li>We send small, friendly test emails to build positive reputation.</li>
                    <li>We monitor opens, replies, and spam reports</li>
                    <li>
                      You can still send transactional emails (receipts, password resets, etc.)
                    </li>
                  </ul>
                </p>
                <p>
                  Once warm-up is complete, you'll be able to send full campaigns and newsletters.
                </p>
                <br />
                <p>
                  If you have any questions, kindly <b>contact support (support@go-mailer.com)</b>.
                </p>
              </>
            )}
          </>
        }
        callback={() => setShowRampWarning(false)}
        is_open={show_ramp_warning}
      />
    </>
  );
};
